import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, doc, deleteDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    IoArrowBack,
    IoDocumentTextOutline,
    IoAddOutline,
    IoTrashOutline,
    IoCalendarOutline,
    IoTimeOutline,
    IoWarningOutline,
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline
} from 'react-icons/io5';
import moment from 'moment';
import './IncidenciasEmpleadoScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function IncidenciasEmpleadoScreen() {
    const { id } = useParams(); // ID del Empleado
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const location = useLocation();

    // Contexto SaaS
    const isSaaS = location.pathname.includes('/superuser/hr');
    const basePath = isSaaS ? '/superuser/hr' : '';

    // Fixed gradient for this screen (Indigo to Blue)
    const gradient = 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)';

    const [empleado, setEmpleado] = useState(null);
    const [incidencias, setIncidencias] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form states
    const [showForm, setShowForm] = useState(false);
    const [tipo, setTipo] = useState('Falta Injustificada');
    const [fecha, setFecha] = useState(moment().format('YYYY-MM-DD'));
    const [cantidad, setCantidad] = useState(1); // 1 día, o 2 horas, etc.
    const [notas, setNotas] = useState('');
    const [saving, setSaving] = useState(false);

    // Modal states
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalTitle, setModalTitle] = useState('');

    useEffect(() => {
        if (!id || !currentUser) return;

        // Fetch empleado base data
        const fetchEmpleado = async () => {
            try {
                const snap = await getDoc(doc(db, "empleados", id));
                if (snap.exists()) setEmpleado(snap.data());
            } catch (err) { console.error(err); }
        };
        fetchEmpleado();

        // Listen to incidencias
        const q = query(
            collection(db, `empleados/${id}/incidencias`),
            orderBy('fecha', 'desc')
        );

        const unsub = onSnapshot(q, (snap) => {
            setIncidencias(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => unsub();
    }, [id, currentUser]);

    const handleAddIncidencia = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await addDoc(collection(db, `empleados/${id}/incidencias`), {
                tipo,
                fecha: Timestamp.fromDate(new Date(fecha + 'T12:00:00')), // Noon to avoid tz shifts
                cantidad: Number(cantidad),
                notas: notas.trim(),
                createdAt: Timestamp.now(),
                estatus: 'Pendiente Aplicación' // Será procesada en la nómina
            });
            setShowForm(false);
            setNotas('');
            setCantidad(1);
            setModalTitle("Incidencia Registrada");
            setModalMessage("El evento ha sido registrado exitosamente y se verá reflejado en el cálculo del siguiente periodo.");
            setShowSuccessModal(true);
        } catch (error) {
            console.error(error);
            alert("Error al guardar incidencia.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (incidenciaId) => {
        if (!window.confirm("¿Borrar esta incidencia? Si ya se aplicó en una nómina cerrada, esto no la recalculará automáticamente.")) return;
        try {
            await deleteDoc(doc(db, `empleados/${id}/incidencias`, incidenciaId));
            setModalTitle("Incidencia Eliminada");
            setModalMessage("La incidencia ha sido eliminada del expediente del empleado.");
            setShowSuccessModal(true);
        } catch (error) {
            console.error(error);
            alert("Error al eliminar.");
        }
    };

    const getIconForType = (tipo) => {
        if (tipo.includes('Falta') || tipo.includes('Retardo')) return <IoWarningOutline className="text-danger" style={{ color: '#ef4444' }} />;
        if (tipo.includes('Horas')) return <IoTimeOutline className="text-primary" style={{ color: '#3b82f6' }} />;
        if (tipo.includes('Vacaciones') || tipo.includes('Permiso')) return <IoCalendarOutline className="text-success" style={{ color: '#10b981' }} />;
        return <IoDocumentTextOutline />;
    };

    if (loading) return <div className="incidencias-loading">Cardando expediente...</div>;

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/superuser/hr" />
            
            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area">
                    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>

                        {/* Premium Fixed Header Banner - Indigo Gradient */}
                        <div style={{
                            background: gradient,
                            position: 'relative',
                            overflow: 'hidden',
                            padding: '30px 25px',
                            borderRadius: '24px',
                            marginBottom: '30px',
                            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 2, position: 'relative', width: '100%', flexWrap: 'wrap', gap: '15px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`${basePath}/gestion-empleados`)}
                                        style={{
                                            background: 'rgba(255,255,255,0.2)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            borderRadius: '50%',
                                            width: '44px',
                                            height: '44px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            color: 'white',
                                            flexShrink: 0
                                        }}
                                        className="encuestas-glass-btn"
                                    >
                                        <IoArrowBack size={22} />
                                    </button>
                                    <div>
                                        <h1 style={{ color: 'white', margin: '0 0 6px', fontSize: '1.7rem', fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>
                                            {isSaaS ? 'Bitácora Corporativa' : 'Incidencias y Permisos'}
                                        </h1>
                                        <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.95rem', fontWeight: '500' }}>
                                            {empleado ? `${empleado.nombre} ${empleado.apellidos}` : 'Historial del empleado'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    className="encuestas-glass-btn"
                                    onClick={() => setShowForm(!showForm)}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px',
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        color: 'white',
                                        borderRadius: '12px',
                                        padding: '10px 18px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {showForm ? <><IoCloseCircleOutline size={20} /> Cerrar Formulario</> : <><IoAddOutline size={20} /> Registrar Incidencia</>}
                                </button>
                            </div>
                            <div style={{ position: 'absolute', right: '-20px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.12 }}>
                                <IoDocumentTextOutline size={180} color="white" />
                            </div>
                        </div>

                        {/* FORMULARIO DESPLEGABLE */}
                        {showForm && (
                            <div style={{...sectionCardStyle, marginBottom: '25px'}} className="glass-card">
                                <h3 style={sectionHeaderStyle}>Registrar Nueva Incidencia / Evento</h3>
                                <form onSubmit={handleAddIncidencia} className="incidencia-form">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                                        <div>
                                            <label style={labelStyle}>Tipo de Evento</label>
                                            <select value={tipo} onChange={e => setTipo(e.target.value)} style={selectStyle}>
                                                <option value="Falta Injustificada">Falta Injustificada (Descuenta Día)</option>
                                                <option value="Retardo">Retardo</option>
                                                <option value="Incapacidad IMSS">Incapacidad Validada (IMSS)</option>
                                                <option value="Permiso Sin Goce">Permiso Sin Goce de Sueldo</option>
                                                <option value="Permiso Con Goce">Permiso Con Goce (+ Paga normal)</option>
                                                <option value="Vacaciones Tomadas">Días de Vacaciones Tomadas</option>
                                                <option value="Horas Extras Dobles">Horas Extras Dobles (Cantidad en hrs)</option>
                                                <option value="Horas Extras Triples">Horas Extras Triples (Cantidad en hrs)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Fecha del Evento</label>
                                            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={inputStyle} required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Cantidad (Ej: 1 día, 2 hrs)</label>
                                            <input type="number" step="0.5" min="0.5" value={cantidad} onChange={e => setCantidad(e.target.value)} style={inputStyle} required />
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <label style={labelStyle}>Notas / Justificación</label>
                                            <input type="text" value={notas} onChange={e => setNotas(e.target.value)} style={inputStyle} placeholder="Ej. Llevó receta médica, o tráfico en periférico" />
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            type="submit"
                                            disabled={saving}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                fontSize: '1rem',
                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                fontWeight: '700',
                                                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {saving ? 'Guardando Incidencia...' : 'Registrar Evento en Nómina'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* TABLA DE INCIDENCIAS HISTORICAS */}
                        <div style={{ background: 'transparent', boxShadow: 'none', border: 'none', padding: 0 }} className="glass-card">
                            
                            {incidencias.length === 0 ? (
                                <div style={{...sectionCardStyle, textAlign: 'center', padding: '35px', color: '#64748b', fontSize: '0.95rem'}}>
                                    No hay incidencias registradas en el expediente de este empleado.
                                </div>
                            ) : (
                                <>
                                    {/* Desktop Table View */}
                                    <div style={sectionCardStyle} className="desktop-table-view table-responsive">
                                        <table className="ag-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                                    <th style={thStyle}>Tipo</th>
                                                    <th style={thStyle}>Fecha</th>
                                                    <th style={thStyle}>Cantidad</th>
                                                    <th style={thStyle}>Notas</th>
                                                    <th style={thStyle}>Estatus Nómina</th>
                                                    <th style={thStyle}>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {incidencias.map(inc => (
                                                    <tr key={inc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={tdStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#0f172a', fontSize: '0.9rem' }}>
                                                                {getIconForType(inc.tipo)} {inc.tipo}
                                                            </div>
                                                        </td>
                                                        <td style={tdStyle}>{moment(inc.fecha.toDate()).format('DD/MMM/YYYY')}</td>
                                                        <td style={tdStyle}><strong>{inc.cantidad}</strong> {inc.tipo.includes('Horas') ? 'hrs' : 'días'}</td>
                                                        <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.85rem', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc.notes || inc.notas || '-'}</td>
                                                        <td style={tdStyle}>
                                                            <span style={badgeStyle(inc.estatus === 'Aplicada')} className={`status-badge ${inc.estatus === 'Aplicada' ? 'bg-success' : 'bg-warning'}`}>
                                                                {inc.estatus === 'Aplicada' ? <IoCheckmarkCircleOutline style={{ marginRight: '4px' }} /> : <IoTimeOutline style={{ marginRight: '4px' }} />} {inc.estatus}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                            <button
                                                                disabled={inc.estatus === 'Aplicada'}
                                                                onClick={() => handleDelete(inc.id)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: inc.estatus === 'Aplicada' ? '#cbd5e1' : '#ef4444',
                                                                    cursor: inc.estatus === 'Aplicada' ? 'not-allowed' : 'pointer',
                                                                    padding: '6px',
                                                                    borderRadius: '8px',
                                                                    transition: 'all 0.2s',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                                title={inc.estatus === 'Aplicada' ? 'No se puede borrar; nómina bloqueada' : 'Eliminar'}
                                                            >
                                                                <IoTrashOutline size={18} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Cards View */}
                                    <div className="incidencias-mobile-list">
                                        {incidencias.map(inc => (
                                            <div key={inc.id} className="incidencia-mobile-card" style={{
                                                background: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '20px',
                                                padding: '20px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '14px',
                                                position: 'relative'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>
                                                        {getIconForType(inc.tipo)} {inc.tipo}
                                                    </div>
                                                    <span style={badgeStyle(inc.estatus === 'Aplicada')}>
                                                        {inc.estatus}
                                                    </span>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '12px 16px', borderRadius: '12px' }}>
                                                    <div>
                                                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Fecha</span>
                                                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>{moment(inc.fecha.toDate()).format('DD / MMM / YYYY')}</span>
                                                    </div>
                                                    <div>
                                                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Cantidad</span>
                                                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>{inc.cantidad} {inc.tipo.includes('Horas') ? 'hrs' : 'días'}</span>
                                                    </div>
                                                </div>

                                                {(inc.notes || inc.notas) && (
                                                    <div style={{ fontSize: '0.85rem', color: '#475569', background: '#fef3c7', padding: '10px 14px', borderRadius: '10px', borderLeft: '4px solid #f59e0b', fontStyle: 'italic' }}>
                                                        "{inc.notes || inc.notas}"
                                                    </div>
                                                )}

                                                {inc.estatus !== 'Aplicada' && (
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                                                        <button
                                                            onClick={() => handleDelete(inc.id)}
                                                            style={{
                                                                background: '#fef2f2',
                                                                border: 'none',
                                                                color: '#ef4444',
                                                                cursor: 'pointer',
                                                                padding: '8px 16px',
                                                                borderRadius: '10px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <IoTrashOutline size={16} /> Eliminar
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                    </div>
                </div>
            </main>

            {/* Custom Success Modal */}
            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(6px)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 99999
                }}>
                    <div style={{
                        background: 'white', padding: '35px 30px', borderRadius: '28px',
                        width: '90%', maxWidth: '400px', textAlign: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        fontFamily: "'Inter', sans-serif"
                    }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            backgroundColor: '#dcfce7', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px', color: '#16a34a',
                            boxShadow: '0 8px 16px rgba(22, 163, 74, 0.15)'
                        }}>
                            <IoCheckmarkCircleOutline size={36} />
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.4rem', fontWeight: '800', color: '#0f172a' }}>
                            {modalTitle}
                        </h3>
                        <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                            {modalMessage}
                        </p>
                        <button
                            onClick={() => setShowSuccessModal(false)}
                            style={{
                                width: '100%', padding: '14px 24px', border: 'none',
                                borderRadius: '14px', background: '#3b82f6', color: 'white',
                                fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            <KondoBottomNav activeRoute="/superuser/hr" />
        </div>
    );
}

// Internal styles matching the premium look
const sectionCardStyle = { 
    background: 'white', 
    padding: '28px', 
    borderRadius: '24px', 
    border: '1px solid #f1f5f9', 
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)' 
};

const sectionHeaderStyle = { 
    margin: '0 0 24px', 
    color: '#0f172a',
    fontSize: '1.15rem',
    fontWeight: '800',
    fontFamily: "'Manrope', sans-serif"
};

const labelStyle = { 
    display: 'block', 
    fontSize: '0.85rem', 
    color: '#475569', 
    marginBottom: '8px', 
    fontWeight: '600' 
};

const inputStyle = { 
    width: '100%', 
    padding: '12px 16px', 
    border: '1px solid #cbd5e1', 
    borderRadius: '12px', 
    fontSize: '0.95rem', 
    color: '#1e293b', 
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    outline: 'none',
    backgroundColor: '#fafafa'
};

const selectStyle = { 
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='%2364748b' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '40px'
};

const thStyle = {
    padding: '12px 16px',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    color: '#64748b',
    fontWeight: '700',
    letterSpacing: '0.03em'
};

const tdStyle = {
    padding: '16px',
    fontSize: '0.9rem',
    color: '#334155'
};

const badgeStyle = (isApplied) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: isApplied ? '#dcfce7' : '#fef3c7',
    color: isApplied ? '#166534' : '#854d0e',
    border: '1px solid ' + (isApplied ? '#bbf7d0' : '#fde68a')
});

export default IncidenciasEmpleadoScreen;
